/**
 * CNPJ — numérico e alfanumérico.
 *
 * A Receita Federal passou a emitir CNPJ com os 12 primeiros caracteres
 * alfanuméricos (0-9 e A-Z), mantendo os 2 últimos como dígitos verificadores
 * numéricos. O tamanho continua 14. Os CNPJs antigos, só com dígitos, seguem
 * válidos — este módulo trata os dois com o mesmo código.
 *
 * Regra do dígito verificador: cada caractere vale o seu código ASCII menos 48,
 * o que faz "0"→0 … "9"→9 e "A"→17 … "Z"→42. Daí em diante é o mesmo módulo 11
 * de sempre, com os mesmos pesos — por isso um CNPJ antigo passa por aqui sem
 * tratamento especial.
 *
 * Duas regras que valem para todo o sistema:
 *
 * 1. **CNPJ é texto, nunca número.** `Number("12ABC345")` é `NaN`, e mesmo um
 *    CNPJ só de dígitos perde o zero à esquerda ao virar número.
 * 2. **Normalizar não remove letras.** `replace(/\D/g, "")` era o padrão antigo
 *    e agora mutila o dado: apaga silenciosamente as letras e deixa um CNPJ
 *    curto que nenhuma validação aceita.
 */

/** Tamanho do CNPJ, com ou sem letras. */
export const CNPJ_LENGTH = 14;

/** Quantidade de caracteres que podem ser alfanuméricos (os demais são DV). */
const BASE_LENGTH = 12;

/** Valor de um caractere para o cálculo do dígito verificador. */
function charValue(ch: string): number {
  return ch.charCodeAt(0) - 48;
}

/**
 * Deixa o CNPJ na forma de armazenamento: sem pontuação, em maiúsculas.
 *
 * Preserva letras de propósito. Caracteres fora de `[0-9A-Z]` são descartados,
 * o que remove a máscara (`.`, `/`, `-`) e o espaço, mas nunca o conteúdo.
 */
export function normalizeCNPJ(value: string | null | undefined): string {
  if (!value) return "";
  return value.toUpperCase().replace(/[^0-9A-Z]/g, "");
}

/** True quando o CNPJ tem ao menos uma letra nas 12 primeiras posições. */
export function isAlphanumericCNPJ(value: string | null | undefined): boolean {
  const cnpj = normalizeCNPJ(value);
  return /[A-Z]/.test(cnpj.slice(0, BASE_LENGTH));
}

/**
 * Aplica a máscara 00.000.000/0000-00, aceitando letras nas 12 primeiras
 * posições. Serve para uso em campo controlado enquanto a pessoa digita.
 */
export function formatCNPJ(value: string | null | undefined): string {
  const cnpj = normalizeCNPJ(value).slice(0, CNPJ_LENGTH);
  if (!cnpj) return "";

  // As duas últimas posições só aceitam dígito; as 12 primeiras, letra ou dígito.
  const base = cnpj.slice(0, BASE_LENGTH);
  const dv = cnpj.slice(BASE_LENGTH).replace(/[^0-9]/g, "");
  const limpo = base + dv;

  return limpo
    .replace(/^(.{2})(.)/, "$1.$2")
    .replace(/^(.{2}\..{3})(.)/, "$1.$2")
    .replace(/^(.{2}\..{3}\..{3})(.)/, "$1/$2")
    .replace(/^(.{2}\..{3}\..{3}\/.{4})(.)/, "$1-$2");
}

/** Calcula um dígito verificador sobre a base informada. */
function checkDigit(base: string): number {
  let peso = base.length - 7;
  let soma = 0;
  for (let i = 0; i < base.length; i++) {
    soma += charValue(base[i]) * peso--;
    if (peso < 2) peso = 9;
  }
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

/**
 * Valida CNPJ numérico ou alfanumérico.
 *
 * Vazio devolve `true` — o campo é opcional em vários formulários, e exigir
 * preenchimento é decisão de cada tela, não desta função.
 */
export function validateCNPJ(value: string | null | undefined): boolean {
  if (!value) return true;
  const cnpj = normalizeCNPJ(value);

  if (cnpj.length !== CNPJ_LENGTH) return false;
  // Os 12 primeiros aceitam letra ou dígito; os 2 últimos, apenas dígito.
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(cnpj)) return false;
  // Sequência de um caractere só (00000000000000, AAAAAAAAAAAA00) não é CNPJ.
  if (/^(.)\1{11}/.test(cnpj)) return false;

  const base = cnpj.slice(0, BASE_LENGTH);
  const d1 = checkDigit(base);
  const d2 = checkDigit(base + d1);
  return cnpj.slice(BASE_LENGTH) === `${d1}${d2}`;
}
